import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Auth.css';

// ---------------------------------------------------------------------------
// Password validation
// ---------------------------------------------------------------------------
function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least 1 uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least 1 number';
  return null;
}

function getRequestErrorMessage(err: any) {
  if (err?.code === 'ECONNABORTED') {
    return 'Server took too long to respond. Please try again in a moment.';
  }
  if (err?.message === 'Network Error' || !err?.response) {
    return 'Cannot reach the server right now. Please check your connection and try again.';
  }
  return err?.response?.data?.message || 'Request failed';
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');

  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp]                         = useState('');
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [message, setMessage]                 = useState('');

  const displacedReason = searchParams.get('reason');

  // ── Sign Up: Send OTP ──────────────────────────────────────
  const handleSendSignupOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password || !confirmPassword) { setError('Please enter both passwords'); return; }

    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }

    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await api.post('/auth/send-signup-otp', { email });
      setMessage('OTP sent to your email');
      setStep('otp');
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Sign Up: Verify OTP ────────────────────────────────────
  const handleVerifySignupOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-signup-otp', { email, otp, password });
      login(response.data.token);
      navigate('/complete-profile');
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Login ──────────────────────────────────────────────────
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      login(response.data.token);
      navigate(response.data.user.profileCompleted ? '/content' : '/complete-profile');
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot Password: Send OTP ──────────────────────────────
  const handleSendForgotOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email'); return; }

    setLoading(true);
    try {
      const response = await api.post('/auth/forgot-password/send-otp', { email });
      setMessage(response.data.message);
      setStep('otp');
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot Password: Verify OTP → auto login ───────────────
  const handleVerifyForgotOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/forgot-password/verify-otp', { email, otp });
      login(response.data.token);
      navigate(response.data.user.profileCompleted ? '/content' : '/complete-profile');
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Google ─────────────────────────────────────────────────
  const handleGoogleSuccess = async (credentialResponse: any) => {
    setError('');
    try {
      const response = await api.post('/auth/google-signin', {
        credential: credentialResponse.credential
      });
      login(response.data.token);
      navigate(
        (response.data.isNewUser || !response.data.user.profileCompleted)
          ? '/complete-profile'
          : '/content'
      );
    } catch (err: any) {
      setError(getRequestErrorMessage(err));
    }
  };

  const resetForm = () => {
    setEmail(''); setPassword(''); setConfirmPassword('');
    setOtp(''); setError(''); setMessage(''); setStep('credentials');
  };

  const switchMode = (newMode: 'login' | 'signup' | 'forgot') => {
    setMode(newMode);
    resetForm();
  };

  const goHome = () => {
    navigate('/content');
  };

  // ── Heading per mode ───────────────────────────────────────
  const heading = mode === 'login' ? 'Welcome' : mode === 'signup' ? 'Create Account' : 'Reset Password';

  // ── Password strength hint (signup only) ──────────────────
  const renderPasswordHint = () => {
    if (!password) return null;
    const checks = [
      { label: '8+ characters',         ok: password.length >= 8 },
      { label: '1 uppercase letter',     ok: /[A-Z]/.test(password) },
      { label: '1 number',               ok: /[0-9]/.test(password) },
    ];
    const allPassed = checks.every(c => c.ok);
    if (allPassed) return null;
    return (
      <ul className="password-hint-list">
        {checks.map(c => (
          <li key={c.label} className={c.ok ? 'hint-ok' : 'hint-fail'}>
            {c.ok ? '✓' : '✗'} {c.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <div className="auth-container">
        <div className="auth-topbar">
          <button type="button" className="auth-topbar-btn" onClick={goHome}>
            Home
          </button>
        </div>
        <div className="auth-card">

          {displacedReason === 'signed_in_elsewhere' && (
            <div className="info-banner">
              You were signed in on another device. Please log in again.
            </div>
          )}

          <h1>{heading}</h1>

          {error   && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          {/* ── Tabs (login / signup only, not shown in forgot) ── */}
          {mode !== 'forgot' && (
            <div className="auth-tabs">
              <button className={mode === 'login'  ? 'active' : ''} onClick={() => switchMode('login')}>Login</button>
              <button className={mode === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')}>Sign Up</button>
            </div>
          )}

          {/* ══ LOGIN ══ */}
          {mode === 'login' && (
            <form onSubmit={handlePasswordLogin} className="auth-form">
              <input
                type="email" placeholder="Email" value={email}
                onChange={e => setEmail(e.target.value)} disabled={loading} required
              />
              <input
                type="password" placeholder="Password" value={password}
                onChange={e => setPassword(e.target.value)} disabled={loading} required
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>

              {/* Forgot password link */}
              <button
                type="button"
                className="secondary"
                onClick={() => switchMode('forgot')}
                disabled={loading}
              >
                Forgot Password?
              </button>
            </form>
          )}

          {/* ══ SIGN UP ══ */}
          {mode === 'signup' && (
            <>
              {step === 'credentials' ? (
                <form onSubmit={handleSendSignupOTP} className="auth-form">
                  <input
                    type="email" placeholder="Email" value={email}
                    onChange={e => setEmail(e.target.value)} disabled={loading} required
                  />
                  <input
                    type="password" placeholder="Password" value={password}
                    onChange={e => setPassword(e.target.value)} disabled={loading} required
                  />
                  {renderPasswordHint()}
                  <input
                    type="password" placeholder="Confirm Password" value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)} disabled={loading} required
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending OTP...' : 'Sign Up'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifySignupOTP} className="auth-form">
                  <p className="otp-instruction">
                    We've sent a 6-digit OTP to <strong>{email}</strong>
                  </p>
                  <input
                    type="text" placeholder="Enter 6-digit OTP" value={otp}
                    onChange={e => setOtp(e.target.value)} maxLength={6}
                    disabled={loading} required
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify & Create Account'}
                  </button>
                  <button type="button" className="secondary" onClick={() => setStep('credentials')} disabled={loading}>
                    ← Back
                  </button>
                </form>
              )}
            </>
          )}

          {/* ══ FORGOT PASSWORD ══ */}
          {mode === 'forgot' && (
            <>
              {step === 'credentials' ? (
                <form onSubmit={handleSendForgotOTP} className="auth-form">
                  <p className="otp-instruction">
                    Enter your email and we'll send a one-time code to log you in.
                  </p>
                  <input
                    type="email" placeholder="Email" value={email}
                    onChange={e => setEmail(e.target.value)} disabled={loading} required
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                  <button type="button" className="secondary" onClick={() => switchMode('login')} disabled={loading}>
                    ← Back to Login
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyForgotOTP} className="auth-form">
                  <p className="otp-instruction">
                    Enter the 6-digit code sent to <strong>{email}</strong>
                  </p>
                  <input
                    type="text" placeholder="Enter 6-digit OTP" value={otp}
                    onChange={e => setOtp(e.target.value)} maxLength={6}
                    disabled={loading} required
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify & Login'}
                  </button>
                  <button type="button" className="secondary" onClick={() => setStep('credentials')} disabled={loading}>
                    ← Back
                  </button>
                </form>
              )}
            </>
          )}

          {/* Google sign-in — hidden on forgot password screen */}
          {mode !== 'forgot' && (
            <>
              <div className="divider">OR</div>
              <div className="google-signin">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in failed. Please try again.')}
                  text={mode === 'login' ? 'signin_with' : 'signup_with'}
                  useOneTap
                />
              </div>
            </>
          )}

        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
